import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";

import { useSession } from "@/app/session.tsx";
import { LoginRoute } from "@/routes/Login.tsx";
import { HomeRoute } from "@/routes/Home.tsx";
import { WorkspaceRoute } from "@/routes/Workspace.tsx";
import { CollectionsRoute } from "@/routes/Collections.tsx";
import { CollectionDetailRoute } from "@/routes/CollectionDetail.tsx";
import { LibraryRoute } from "@/routes/Library.tsx";
import { QueryTrendsRoute } from "@/routes/QueryTrends.tsx";

/**
 * Auth is a render-time gate, not a loader redirect: the session is a React Query
 * resource, and blocking navigation on it would flash the login screen on every hard
 * reload while `/api/auth/me` is in flight.
 */
function Shell() {
  const { user, isLoading } = useSession();
  if (isLoading) {
    return (
      <div className="login-main">
        <p className="hint">Memuat…</p>
      </div>
    );
  }
  if (!user) return <LoginRoute />;
  return <Outlet />;
}

const rootRoute = createRootRoute({ component: Shell });

const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: HomeRoute });

const studioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/studio",
  component: WorkspaceRoute,
  // The key must be OPTIONAL in the returned type, not `string | undefined`. With the
  // latter TanStack treats `search` as required on every link to /studio, so the nav
  // bar cannot link to the studio without inventing a search param.
  validateSearch: (search: Record<string, unknown>): { collection?: string } =>
    typeof search.collection === "string" ? { collection: search.collection } : {},
});

const studioConversationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/studio/$conversationId",
  component: WorkspaceRoute,
});

const collectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/collections",
  component: CollectionsRoute,
});

const collectionDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/collections/$collectionId",
  component: CollectionDetailRoute,
});

const libraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/library",
  component: LibraryRoute,
});

const queryTrendsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/query-trends",
  component: QueryTrendsRoute,
});

const catchAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "$",
  beforeLoad: () => {
    throw redirect({ to: "/" });
  },
  component: HomeRoute,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  studioRoute,
  studioConversationRoute,
  collectionsRoute,
  collectionDetailRoute,
  libraryRoute,
  queryTrendsRoute,
  catchAllRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: false });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
